"use client";

import { Button, cn } from "@aio/platform-ui";
import { Check } from "lucide-react";
import * as React from "react";

export interface WizardStep {
  id: string;
  label: string;
  content: React.ReactNode;
}

export interface WizardPageProps {
  title: string;
  description?: string;
  steps: readonly WizardStep[];
  currentStepId: string;
  onChangeStep: (id: string) => void;
  onNext?: () => void;
  onPrev?: () => void;
  onFinish?: () => void;
  /** Optional flag: caller controls when "Finish" replaces "Next" */
  isLastStep?: boolean;
}

export function WizardPage({
  title,
  description,
  steps,
  currentStepId,
  onChangeStep,
  onNext,
  onPrev,
  onFinish,
  isLastStep,
}: WizardPageProps) {
  const currentIdx = steps.findIndex((s) => s.id === currentStepId);
  const current = steps[currentIdx];
  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
      </header>
      <ol className="flex items-center gap-2 overflow-x-auto" aria-label="Wizard progress">
        {steps.map((s, i) => {
          const done = i < currentIdx;
          const active = s.id === currentStepId;
          return (
            <li key={s.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onChangeStep(s.id)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm",
                  active && "bg-accent/10 text-accent-strong",
                  done && "text-fg",
                  !active && !done && "text-fg-muted",
                )}
              >
                <span
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-xs font-semibold",
                    active && "bg-accent text-accent-fg",
                    done && "bg-success text-success-fg",
                    !active && !done && "bg-muted text-fg-muted",
                  )}
                >
                  {done ? <Check className="size-3.5" aria-hidden="true" /> : i + 1}
                </span>
                <span>{s.label}</span>
              </button>
              {i < steps.length - 1 ? (
                <span className="h-px w-6 bg-border" aria-hidden="true" />
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="rounded-lg border border-border bg-bg-elevated p-6">{current?.content}</div>
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          {...(onPrev ? { onClick: onPrev } : {})}
          disabled={currentIdx === 0}
        >
          Previous
        </Button>
        {isLastStep ? (
          <Button {...(onFinish ? { onClick: onFinish } : {})}>Finish</Button>
        ) : (
          <Button {...(onNext ? { onClick: onNext } : {})}>Next</Button>
        )}
      </div>
    </div>
  );
}
